/**
 * cookieService — thin wrapper around `@hotelb2b/cookie-consent-core`.
 *
 * Migrated under CCF-3/CCF-4/CCF-6 (ADR-074):
 *   - Single source of truth for state machine + API client + GTM Consent Mode v2
 *   - Backend POST payload now includes brand/bannerVersion/policyVersion/consentMethod
 *     (was incomplete → 400 Bad Request)
 *   - Consent Mode v2 update emits ALL 4 signals (was only 2 of 4)
 *   - Retry queue for failed POST/PUT lives in core's api-client.ts
 *
 * Backward-compatible API: callers continue to use cookieService.hasConsent(),
 * cookieService.acceptAll(), etc. — see consumers in Footer, Register, useGTM,
 * PublicOffer, metaPixelService, analyticsService, CookieBanner.
 */
import {
  createCookieConsent,
  type ConsentSnapshot,
  type CookieConsentApi,
} from "@hotelb2b/cookie-consent-core";
import { LEGAL_VERSIONS } from "@hotelb2b/legal-content";
import { getApiUrl } from "./api";

/** Legacy shape consumed by Footer/CookieBanner/PublicOffer for `getLocalConsent()`. */
export interface CookieConsent {
  sessionId: string;
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  expiresAt: string;
}

/**
 * Backend response shape for POST/PUT /cookie-consent (architecture H4 — flat envelope).
 * Matches `ApiResponse` in `@hotelb2b/cookie-consent-core/api-client.ts`.
 */
export interface CookieConsentResponse {
  id: string;
  expiresAt: string;
}

const BANNER_VERSION = "2026-05-20";

/**
 * Translate the core's ConsentSnapshot to the legacy CookieConsent shape.
 * Returns null when there is no recorded consent.
 */
function snapshotToLegacy(snapshot: ConsentSnapshot | null): CookieConsent | null {
  if (!snapshot) return null;
  return {
    sessionId: snapshot.sessionId,
    essential: snapshot.categories.essential,
    analytics: snapshot.categories.analytics,
    marketing: snapshot.categories.marketing,
    expiresAt: snapshot.expiresAt,
  };
}

/**
 * Build the core consent instance.
 * apiBase: core appends `/cookie-consent` → must match backend route prefix.
 */
function buildCore(): CookieConsentApi {
  return createCookieConsent({
    apiBase: getApiUrl(),
    brand: "kiedynabory",
    policyVersion: LEGAL_VERSIONS.PRIVACY,
    bannerVersion: BANNER_VERSION,
  });
}

class CookieService {
  private core: CookieConsentApi;
  private listeners: Array<(consent: CookieConsent | null) => void> = [];
  private unsubscribeCore: (() => void) | null = null;

  constructor() {
    this.core = buildCore();
    // Bridge core's subscribe → legacy callback shape
    this.unsubscribeCore = this.core.subscribe((snapshot) => {
      // Signal the consent-gated GTM loader (public/gtm-loader.js) that consent
      // changed, so it can load the GTM container in-session once the user grants
      // analytics/marketing. The core has already written storage + pushed the
      // Consent Mode v2 signals before this fires (saveAndNotify order), and the
      // loader re-reads consent + self-guards, so dispatching on every change is
      // safe (it no-ops for essential-only).
      if (typeof window !== "undefined" && snapshot) {
        window.dispatchEvent(new Event("cookie-consent-updated"));
      }
      const legacy = snapshotToLegacy(snapshot);
      this.listeners.forEach((cb) => {
        try {
          cb(legacy);
        } catch {
          // Swallow listener errors
        }
      });
    });
  }

  // --- Session helpers ------------------------------------------------------

  /**
   * Returns sessionId from the current consent snapshot, or empty string if none.
   * Used by analyticsService to align analytics session with cookie consent.
   */
  getSessionId(): string {
    return this.core.getCurrent()?.sessionId ?? "";
  }

  /**
   * Storage type signal kept for analyticsService back-compat.
   * After migration core uses a single localStorage key; sessionStorage is no
   * longer split, but consumers still want a "local" vs "session" hint.
   */
  getStorageType(): "session" | "local" {
    return this.hasAnalyticsConsent() ? "local" : "session";
  }

  // --- Local state queries --------------------------------------------------

  getLocalConsent(): CookieConsent | null {
    return snapshotToLegacy(this.core.getCurrent());
  }

  hasConsent(): boolean {
    return this.core.getCurrent() !== null;
  }

  hasAnalyticsConsent(): boolean {
    return this.core.getCurrent()?.categories.analytics ?? false;
  }

  hasMarketingConsent(): boolean {
    return this.core.getCurrent()?.categories.marketing ?? false;
  }

  hasEssentialConsent(): boolean {
    // Essential cookies are always granted once any consent decision is taken;
    // before consent we default to `true` (legacy behavior).
    return this.core.getCurrent()?.categories.essential ?? true;
  }

  shouldShowBanner(): boolean {
    return !this.hasConsent();
  }

  // --- Consent actions ------------------------------------------------------

  async acceptAll(): Promise<void> {
    await this.core.acceptAll();
  }

  async acceptEssentialOnly(): Promise<void> {
    await this.core.essentialOnly();
  }

  async acceptCustom(analytics: boolean, marketing: boolean): Promise<void> {
    await this.core.setCustom({ analytics, marketing });
  }

  // --- Listener registry (back-compat) --------------------------------------

  onConsentChange(callback: (consent: CookieConsent | null) => void): () => void {
    this.listeners.push(callback);
    return () => {
      const idx = this.listeners.indexOf(callback);
      if (idx > -1) this.listeners.splice(idx, 1);
    };
  }

  /** Used in unit tests to fully tear down the singleton. */
  _disposeForTests(): void {
    this.unsubscribeCore?.();
    this.unsubscribeCore = null;
    this.listeners = [];
  }
}

export const cookieService = new CookieService();
export default cookieService;
